import { useCallback } from "react";

import { ApolloError, useApolloClient } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import { useNavigate } from "react-router";

import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { routes } from "config/routes";
import { PickingConflictData } from "flows/Picking/components/PickingConflictModal";
import { Order } from "flows/Picking/models/order/types";
import {
  FinishPickingOrderDocument,
  FinishPickingOrderMutation,
  FinishPickingOrderMutationVariables,
} from "flows/Picking/queries/order/order.generated";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";

import { usePickingUIStore } from "./usePickingUIStore";
import { PickingOrigin } from "../stores/pickingTypes";
import { getMissingItemsFromContext, getItemWithPickingState } from "../stores/pickingUtils";
import { usePickingStore, PickingStoreState } from "../stores/usePickingStore";
import { useScanProgressStore } from "../stores/useScanProgressStore";
import { useVariablePriceStore } from "../stores/useVariablePriceStore";
import { isPickingConflictError, extractConflictData } from "../utils/pickingConflict";

// Keeps the order out of the next-order slot on the picking start page once it is packed.
function markOrderPacked(state: PickingStoreState) {
  if (state.order?.id) useAppBackgroundEffectsStore.getState().markOrderPacked(state.order.id);
}

export function usePickingActions() {
  const navigate = useNavigate();
  const { sendSegmentTrackEvent } = useAnalytics();
  const setPickingUIState = usePickingUIStore((state) => state.setPickingUIState);
  const client = useApolloClient();
  const { showToastAndPlayError } = useErrorToastWithAudio({
    errorToastId: "end_picking_error_toast",
  });

  // --- Analytics helpers ---

  const getOrderMeta = (state: PickingStoreState) => {
    const order = state.order as Record<string, unknown> | null;
    return {
      order_number: order?.number as string | undefined,
      order_id: order?.id as string | undefined,
    };
  };

  const trackOrderPicked = useCallback(
    (state: PickingStoreState) => {
      sendSegmentTrackEvent("orderStateUpdated", {
        state: "picked",
        origin: state.origin,
        ...getOrderMeta(state),
      });
    },
    [sendSegmentTrackEvent],
  );

  const trackOrderPacked = useCallback(
    (state: PickingStoreState) => {
      sendSegmentTrackEvent("orderStateUpdated", {
        state: "packed",
        origin: state.origin,
        ...getOrderMeta(state),
      });
    },
    [sendSegmentTrackEvent],
  );

  const trackOrderPickingFinished = useCallback(
    (state: PickingStoreState) => {
      sendSegmentTrackEvent("orderStateUpdated", {
        state: "picking_finished",
        origin: state.origin,
        ...getOrderMeta(state),
      });
    },
    [sendSegmentTrackEvent],
  );

  const sendSegmentOrderProgressed = useCallback(
    (
      state: PickingStoreState,
      event:
        | {
            type: "INCREMENT_ITEM_QUANTITY";
            itemId: string;
            isSkippedItem: boolean;
            reportedBarcodeIssue: boolean;
            pickedByScan?: boolean;
            scannedBarcode?: string;
          }
        | { type: "SKIP_ITEM"; itemId: string; isSkippedItem: boolean }
        | { type: "RESET_ITEM"; itemId: string },
    ) => {
      const { order_id: orderId, order_number: orderNumber } = getOrderMeta(state);
      if (!orderId || !orderNumber) return;

      const product = getItemWithPickingState(
        event.itemId,
        state,
        event.type === "SKIP_ITEM" ? event.isSkippedItem : false,
      );
      if (!product) return;

      let action;
      let pickingMethod;
      let quantity;

      switch (event.type) {
        case "INCREMENT_ITEM_QUANTITY":
          action = "item_picked";
          quantity = 1;
          if (event.reportedBarcodeIssue) {
            pickingMethod = "reported";
          } else if (event.pickedByScan) {
            pickingMethod = "scanned";
          } else {
            pickingMethod = "clicked";
          }
          break;
        case "SKIP_ITEM":
          action = event.isSkippedItem ? "item_refunded" : "item_skipped";
          quantity = product.item.quantity - product.pickedQuantity;
          break;
        case "RESET_ITEM":
          action = "item_reset";
          quantity = product.pickedQuantity;
          break;
        default:
          return;
      }

      const productPricingType = product.item.hasVariablePriceUnit ? "weight_based" : "fixed";

      const alertIssueType = product.item.smartPickingAlertIssueType;

      sendSegmentTrackEvent("orderProgressed", {
        action,
        order_id: orderId,
        order_number: orderNumber,
        ean: product.item.eans,
        product_sku: product.item.sku,
        quantity: quantity > 0 ? quantity : 0,
        method: pickingMethod,
        reason: null,
        scanned_barcode:
          event.type === "INCREMENT_ITEM_QUANTITY" ? (event.scannedBarcode ?? null) : null,
        product_pricing_type: productPricingType,
        alert_shown: alertIssueType !== null,
        alert_issue_type: alertIssueType,
      });
    },
    [sendSegmentTrackEvent],
  );

  const sendSegmentContainerStatus = useCallback(
    (state: PickingStoreState) => {
      if (state.containersIds.length) {
        sendSegmentTrackEvent("containerAssigned", {
          type: "Container",
          method: state.inputMethod === "MANUAL" ? "manual" : "scanned",
          container_ids: state.containersIds,
          ...getOrderMeta(state),
        });
        return;
      }
      sendSegmentTrackEvent("containerAssignmentSkipped", {
        type: "Container",
        ...getOrderMeta(state),
      });
    },
    [sendSegmentTrackEvent],
  );

  const sendSegmentShelfStatus = useCallback(
    (state: PickingStoreState) => {
      if (state.shelvesIds.length) {
        sendSegmentTrackEvent("containerAssigned", {
          type: "Shelf",
          container_ids: state.shelvesIds,
          method: state.inputMethod === "MANUAL" ? "manual" : "scanned",
          ...getOrderMeta(state),
        });
        return;
      }
      sendSegmentTrackEvent("containerAssignmentSkipped", {
        type: "Shelf",
        ...getOrderMeta(state),
      });
    },
    [sendSegmentTrackEvent],
  );

  // --- Async endPicking mutation ---

  const callEndPickingMutation = useCallback(
    async (state: PickingStoreState) => {
      const { order_id: orderId } = getOrderMeta(state);
      const missingItems = getMissingItemsFromContext(state);

      try {
        if (!orderId) throw new Error("Error: finishPickingOrder : Missing orderId");

        const variablePriceItems = useVariablePriceStore.getState().variablePriceItems;
        const variablePrices = Object.values(variablePriceItems)
          .flat()
          .map((entry) => ({ sku: entry.sku, price: entry.priceInCents }));

        const data = {
          orderId,
          missingItems,
          containerIds: state.containersIds,
          shelfNumbers: state.shelvesIds,
          ...(variablePrices.length > 0 && { variablePrices }),
        };

        datadogLogs.logger.info("finishPickingOrder called with following args", data);
        datadogLogs.logger.info("finishPickingOrder full picking state", {
          orderId,
          missingItems,
          itemsPickingState: state.itemsPickingState,
          skippedItemsPickingState: state.skippedItemsPickingState,
          itemsMap: Object.fromEntries(
            Object.entries(state.itemsMap).map(([k, v]) => [
              k,
              { sku: v.sku, quantity: v.quantity },
            ]),
          ),
          itemsIdsSortedByShelf: state.itemsIdsSortedByShelf,
          skippedItemsIdsSortedByShelf: state.skippedItemsIdsSortedByShelf,
        });
        const result = await client.mutate<
          FinishPickingOrderMutation,
          FinishPickingOrderMutationVariables
        >({
          mutation: FinishPickingOrderDocument,
          variables: data,
        });
        return result.data?.finishPickingOrder.message ?? null;
      } catch (error) {
        if (isPickingConflictError(error)) {
          throw Object.assign(new Error("pickingOrderConflict"), {
            conflictData: extractConflictData(error as ApolloError),
          });
        }
        if (
          error instanceof ApolloError &&
          error?.graphQLErrors?.[0]?.extensions?.code === "ORDER_STATE_NOT_ELIGIBLE_FOR_PACKING"
        ) {
          throw new Error("orderStateNotEligibleForPacking");
        }
        showToastAndPlayError({
          title: "components.picking.end-picking-button.error-toast-retry-end-picking",
        });
        throw new Error("Error: endPicking");
      }
    },
    [client, showToastAndPlayError],
  );

  // --- Navigation + state transition actions ---

  const startPicking = useCallback(
    (order: Order, origin: PickingOrigin) => {
      const store = usePickingStore.getState();
      store.setOrder(order);
      store.setOrigin(origin);
      store.setPickingPhase("scanningItems");

      // Initialize scan progress slots for WBP items
      if (order.items) {
        const progressStore = useScanProgressStore.getState();
        for (const item of order.items) {
          if (item.hasVariablePriceUnit) {
            progressStore.initializeSlots(item.id, item.quantity);
          }
        }
      }

      trackOrderPicked(usePickingStore.getState());
      navigate(routes.pickingV2.start);
    },
    [navigate, trackOrderPicked],
  );

  const goToManualPicking = useCallback(() => {
    usePickingStore.getState().setPickingPhase("startManualPicking");
    navigate(routes.pickingV2.manual);
  }, [navigate]);

  const goToScanContainers = useCallback(() => {
    const state = usePickingStore.getState();
    trackOrderPickingFinished(state);
    state.setPickingPhase("scanningContainers");
    navigate(routes.pickingV2.containers);
  }, [navigate, trackOrderPickingFinished]);

  const goToAssignShelves = useCallback(() => {
    const state = usePickingStore.getState();
    sendSegmentContainerStatus(state);
    state.setPickingPhase("assigningShelves");
    navigate(routes.pickingV2.shelves);
  }, [navigate, sendSegmentContainerStatus]);

  const goToStuck = useCallback(() => {
    usePickingStore.getState().setPickingPhase("pickingStuck");
    navigate(routes.pickingV2.incomplete);
  }, [navigate]);

  const goBack = useCallback(() => {
    const state = usePickingStore.getState();
    if (state.pickingPhase === "scanningContainers") {
      state.setPickingPhase("scanningItems");
      navigate(routes.pickingV2.start);
    } else if (state.pickingPhase === "assigningShelves") {
      state.setPickingPhase("scanningContainers");
      navigate(routes.pickingV2.containers);
    }
  }, [navigate]);

  const backToPicking = useCallback(() => {
    usePickingStore.getState().setPickingPhase("scanningItems");
    navigate(routes.pickingV2.start);
  }, [navigate]);

  const finishPicking = useCallback(() => {
    usePickingStore.getState().reset();
    useVariablePriceStore.getState().reset();
    useScanProgressStore.getState().reset();
    usePickingUIStore.getState().reset();
    navigate(routes.pickingV2.root);
  }, [navigate]);

  const confirmOrderCompletion = useCallback(() => {
    usePickingUIStore.getState().reset();
    const state = usePickingStore.getState();
    markOrderPacked(state);
    state.setPickingPhase("finished");
    navigate(routes.pickingV2.end);
  }, [navigate]);

  const executeEndPicking = useCallback(
    async (state: PickingStoreState) => {
      try {
        await callEndPickingMutation(state);
        markOrderPacked(state);
        trackOrderPacked(usePickingStore.getState());
        usePickingStore.setState({ endPickingPhase: "idle", pickingPhase: "finished" });
        navigate(routes.pickingV2.end);
      } catch (error) {
        if ((error as Error)?.message?.includes("pickingOrderConflict")) {
          usePickingStore.setState({ endPickingPhase: "idle" });
          setPickingUIState({
            endPickingConflictData:
              (error as { conflictData?: PickingConflictData | null }).conflictData ?? null,
          });
        } else if ((error as Error)?.message?.includes("orderStateNotEligibleForPacking")) {
          usePickingStore.setState({ endPickingPhase: "idle" });
          setPickingUIState({ shouldShowOrderCompletionWarning: true });
        } else {
          usePickingStore.setState({ endPickingPhase: "error" });
        }
      }
    },
    [navigate, callEndPickingMutation, trackOrderPacked, setPickingUIState],
  );

  const endPicking = useCallback(async () => {
    const state = usePickingStore.getState();

    if (state.endPickingPhase === "loading") return;

    // Track based on current picking phase (parallel state behavior)
    switch (state.pickingPhase) {
      case "scanningItems":
        trackOrderPickingFinished(state);
        break;
      case "scanningContainers":
        sendSegmentContainerStatus(state);
        break;
      case "assigningShelves":
        sendSegmentShelfStatus(state);
        break;
      default:
        break;
    }

    usePickingStore.setState({ endPickingPhase: "loading" });
    await executeEndPicking(state);
  }, [
    executeEndPicking,
    trackOrderPickingFinished,
    sendSegmentContainerStatus,
    sendSegmentShelfStatus,
  ]);

  const retryEndPicking = useCallback(async () => {
    const state = usePickingStore.getState();

    if (state.endPickingPhase === "loading") return;

    usePickingStore.setState({ endPickingPhase: "loading" });
    await executeEndPicking(state);
  }, [executeEndPicking]);

  const resetPicking = useCallback(() => {
    usePickingStore.getState().reset();
    useVariablePriceStore.getState().reset();
    useScanProgressStore.getState().reset();
    usePickingUIStore.getState().reset();
    navigate(routes.pickingV2.root);
  }, [navigate]);

  // --- Item actions with analytics ---

  const incrementItemQuantity = useCallback(
    (
      itemId: string,
      isSkippedItem: boolean,
      reportedBarcodeIssue: boolean,
      pickedByScan?: boolean,
      scannedBarcode?: string,
    ) => {
      usePickingStore
        .getState()
        .incrementItemQuantity(itemId, isSkippedItem, reportedBarcodeIssue, pickedByScan);

      // Update scan progress for WBP items
      const item = usePickingStore.getState().itemsMap[itemId];
      if (item?.hasVariablePriceUnit) {
        const progressStore = useScanProgressStore.getState();
        if (reportedBarcodeIssue) {
          progressStore.markSlotReported(itemId);
        } else {
          progressStore.markSlotScanned(itemId);
        }
      }

      sendSegmentOrderProgressed(usePickingStore.getState(), {
        type: "INCREMENT_ITEM_QUANTITY",
        itemId,
        isSkippedItem,
        reportedBarcodeIssue,
        pickedByScan,
        scannedBarcode,
      });
    },
    [sendSegmentOrderProgressed],
  );

  const decrementItemQuantity = useCallback((itemId: string) => {
    const stateBefore = usePickingStore.getState();
    const isSkippedItem = stateBefore.skippedItemsIdsSortedByShelf.includes(itemId);
    const before = getItemWithPickingState(itemId, stateBefore, isSkippedItem);
    usePickingStore.getState().decrementItemQuantity(itemId);
    const stateAfter = usePickingStore.getState();
    const after = getItemWithPickingState(itemId, stateAfter, isSkippedItem);
    datadogLogs.logger.warn("picking.decrementItemQuantity", {
      itemId,
      isSkippedItem,
      sku: before?.item.sku ?? after?.item.sku ?? null,
      itemQuantity: before?.item.quantity ?? after?.item.quantity ?? null,
      beforePickedQuantity: before?.pickedQuantity ?? null,
      beforeState: before?.state ?? null,
      afterPickedQuantity: after?.pickedQuantity ?? null,
      afterState: after?.state ?? null,
      pickingPhase: stateBefore.pickingPhase,
      viewUrlPath: typeof window !== "undefined" ? window.location.pathname : null,
    });
  }, []);

  const skipItem = useCallback(
    (itemId: string, isSkippedItem: boolean) => {
      const item = usePickingStore.getState().itemsMap[itemId];
      usePickingStore.getState().skipItem(itemId, isSkippedItem);

      if (item?.hasVariablePriceUnit) {
        const progressStore = useScanProgressStore.getState();
        if (isSkippedItem) {
          // Item not found from skipped list → refund remaining (final state)
          progressStore.refundRemainingSlots(itemId);
        } else {
          // Regular skip → reactivate remaining slots so they're
          // ready for picking in the skipped items list
          progressStore.reactivateRemainingSlots(itemId);
        }
      }

      sendSegmentOrderProgressed(usePickingStore.getState(), {
        type: "SKIP_ITEM",
        itemId,
        isSkippedItem,
      });
    },
    [sendSegmentOrderProgressed],
  );

  const resetItem = useCallback(
    (itemId: string) => {
      const stateBefore = usePickingStore.getState();
      sendSegmentOrderProgressed(stateBefore, {
        type: "RESET_ITEM",
        itemId,
      });
      usePickingStore.getState().resetItem(itemId);
    },
    [sendSegmentOrderProgressed],
  );

  return {
    // Navigation actions
    startPicking,
    goToManualPicking,
    goToScanContainers,
    goToAssignShelves,
    goToStuck,
    goBack,
    backToPicking,
    finishPicking,
    confirmOrderCompletion,
    resetPicking,

    // Async actions
    endPicking,
    retryEndPicking,

    // Item actions (with analytics)
    incrementItemQuantity,
    decrementItemQuantity,
    skipItem,
    resetItem,
  };
}
